import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CalendarClock, AlertOctagon, CheckCircle2, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fiscal-year switch (Nov 1): shows admins whether next year's Quote / Work Orders / PO books are linked in time.
export const FiscalSwitchBanner = () => {
  const navigate = useNavigate();
  const [fy, setFy] = useState(null);
  useEffect(() => { axios.get(`${API}/fiscal-books`).then(r => setFy(r.data)).catch(() => {}); }, []);
  if (!fy) return null;
  const soon = fy.days_to_switch <= 21;
  const afterSwitch = !fy.current_ready; // Nov 1 came and the current FY's books are still missing
  if (!afterSwitch && (!soon || fy.next_ready)) return null;
  const target = afterSwitch ? fy.years.find(y => y.year === fy.current_fy) : fy.years.find(y => y.year === fy.next_fy);
  const missing = (fy.kinds || []).filter(k => !(target?.books?.[k.key]?.id)).map(k => k.label);
  const linked = (fy.kinds || []).filter(k => target?.books?.[k.key]?.id).map(k => k.label);
  const yearLong = afterSwitch ? fy.current_fy_long : fy.next_fy_long;
  const tone = afterSwitch ? 'bg-red-50 border-red-500 text-red-900' : fy.days_to_switch <= 7 ? 'bg-red-50 border-red-400 text-red-900' : 'bg-amber-50 border-amber-400 text-amber-900';
  return (
    <button type="button" onClick={() => navigate('/admin?tab=sheets')} data-testid="fiscal-switch-banner"
      className={`w-full text-left rounded-2xl border-2 p-3 flex items-start gap-3 ${tone}`}>
      {afterSwitch ? <AlertOctagon className="w-5 h-5 shrink-0 mt-0.5 animate-pulse" /> : <CalendarClock className="w-5 h-5 shrink-0 mt-0.5" />}
      <span className="flex-1 min-w-0">
        <span className="block text-sm font-extrabold leading-tight">
          {afterSwitch
            ? `FY${yearLong} has started and its books aren't linked — new quotes, work orders and POs are blocked until they are`
            : `${fy.days_to_switch === 0 ? 'Tonight' : `In ${fy.days_to_switch} day${fy.days_to_switch === 1 ? '' : 's'}`} Ops switches to ${fy.next_fy}-XXXX numbers — the ${yearLong} books aren't linked yet`}
        </span>
        <span className="block text-xs mt-1">
          {missing.length > 0 && <span className="font-semibold">Missing: {missing.join(', ')}. </span>}
          {linked.length > 0 && <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> Linked: {linked.join(', ')}. </span>}
          Make the {yearLong} sheets (same layout as {afterSwitch ? fy.years.find(y => y.year === String(Number(fy.current_fy) - 1))?.long : fy.current_fy_long}, pre-numbered rows), share them with the Ops service account, then link them in Admin → Sheet Sync → Fiscal Year Books.
        </span>
      </span>
      <ChevronRight className="w-4 h-4 shrink-0 mt-1" />
    </button>
  );
};
